// Podium: app shell, routing and the home screen.

import { icons } from '../shared/icons.js';

// Application State
let currentPage = 'page-landing';
let currentTheme = 'paper-teal';
let isDarkMode = false;

// Language list for Podium (all supported languages)
const PODIUM_LANGUAGES = [
    { code: 'eng', name: 'English' },
    { code: 'swa', name: 'Swahili' },
    { code: 'kik', name: 'Kikuyu' },
    { code: 'kam', name: 'Kamba' },
    { code: 'mer', name: 'Kimeru' },
    { code: 'luo', name: 'Luo' },
    { code: 'som', name: 'Somali' }
];

const SPEAKER_KEY = 'podium-user-language';
const TARGETS_KEY = 'podium-target-languages';

const htmlElement = document.documentElement;
const pages = document.querySelectorAll('.page-section');

// ---------- Helpers shared with the other Podium screens ----------

export function escapeHtml(value) {
    return String(value ?? '')
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
        .replace(/"/g, '&quot;')
        .replace(/'/g, '&#39;');
}

function languageName(code) {
    return PODIUM_LANGUAGES.find((l) => l.code === code)?.name || code;
}

/** The speaker language and the languages to show, as chosen on the home screen. */
function getTalkSetup() {
    const speaker = localStorage.getItem(SPEAKER_KEY) || 'eng';
    let targets = null;
    try {
        targets = JSON.parse(localStorage.getItem(TARGETS_KEY) || 'null');
    } catch {
        targets = null;
    }
    const valid = new Set(PODIUM_LANGUAGES.map((l) => l.code));
    if (!Array.isArray(targets)) {
        targets = PODIUM_LANGUAGES.map((l) => l.code);
    }
    targets = targets.filter((code) => valid.has(code) && code !== speaker);
    return { speaker, targets };
}

function saveTalkSetup({ speaker, targets }) {
    localStorage.setItem(SPEAKER_KEY, speaker);
    localStorage.setItem(TARGETS_KEY, JSON.stringify(targets));
}

function getSavedSessions() {
    try {
        const sessionsJson = localStorage.getItem('podium-sessions');
        return sessionsJson ? JSON.parse(sessionsJson) : [];
    } catch (error) {
        console.error('Error loading saved sessions:', error);
        return [];
    }
}

/** Title and one-line summary for a saved talk. */
function describeTalk(session) {
    const started = new Date(session.timestamp);
    const title = started.toLocaleString(undefined, {
        weekday: 'short', day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit',
    });
    const ended = new Date(session.lastUpdated || session.timestamp);
    const minutes = Math.max(1, Math.round((ended - started) / 60000));
    const langs = new Set();
    (session.messages || []).forEach((m) => Object.keys(m.translations || {}).forEach((k) => langs.add(k)));
    const lines = session.messageCount ?? (session.messages || []).length;
    const parts = [`${minutes} min`];
    if (langs.size) parts.push(`${langs.size} ${langs.size === 1 ? 'language' : 'languages'}`);
    parts.push(`${lines} ${lines === 1 ? 'line' : 'lines'}`);
    return { title, meta: parts.join(' · ') };
}

// ---------- Theme ----------

function applyTheme(theme, mode) {
    htmlElement.classList.toggle('dark', mode === 'dark');
    currentTheme = theme || currentTheme;
    isDarkMode = mode === 'dark';
    updatePWAThemeColor(currentPage === 'page-landing');
}

/** Title-bar colour: the page ground on Home, the bar surface elsewhere. */
function updatePWAThemeColor(useGround = false) {
    const color = useGround
        ? (isDarkMode ? '#121715' : '#F5F3EE')
        : (isDarkMode ? '#1B211F' : '#FFFFFF');
    let meta = document.querySelector('meta[name="theme-color"]');
    if (!meta) {
        meta = document.createElement('meta');
        meta.name = 'theme-color';
        document.head.appendChild(meta);
    }
    meta.content = color;
}

// ---------- Routing ----------

const router = {
    routes: {
        '/': 'page-landing',
        '/session': 'page-session',
        '/past-sessions': 'page-past-sessions',
        '/settings': 'page-settings'
    },

    getPageIdFromPath(path) {
        return this.routes[path] || 'page-landing';
    },

    getPathFromPageId(pageId) {
        for (const [path, id] of Object.entries(this.routes)) {
            if (id === pageId) return path;
        }
        return '/';
    },

    navigate(path) {
        const pageId = this.getPageIdFromPath(path);
        showPage(pageId, false);
        window.history.pushState({ pageId }, '', path);
    },

    handlePopState(event) {
        const pageId = event.state?.pageId || this.getPageIdFromPath(window.location.pathname);
        showPage(pageId, false);
    }
};

function showPage(pageId, updateHistory = true) {
    const targetPage = document.getElementById(pageId);
    if (!targetPage) return;

    // Leaving a live talk stops the microphone first.
    if (currentPage === 'page-session' && pageId !== 'page-session' && typeof window.leavesession === 'function') {
        window.leavesession();
    }

    pages.forEach((page) => page.classList.remove('active'));
    targetPage.classList.add('active');
    currentPage = pageId;

    if (updateHistory) {
        window.history.pushState({ pageId }, '', router.getPathFromPageId(pageId));
    }

    updatePWAThemeColor(pageId === 'page-landing');

    const initFunctionName = `init${pageId.replace('page-', '').replace(/-/g, '')}`;
    if (typeof window[initFunctionName] === 'function') {
        window[initFunctionName]();
    }
}

function setupRoutes() {
    window.addEventListener('popstate', (event) => router.handlePopState(event));
    const currentPath = window.location.pathname;
    window.history.replaceState({ pageId: router.getPageIdFromPath(currentPath) }, '', currentPath);
}

// ---------- Home ----------

let serviceHealthChecked = false;

function setState(html) {
    const state = document.getElementById('landing-state');
    const content = document.getElementById('landing-content');
    if (!state || !content) return;
    if (html === null) {
        state.hidden = true;
        content.hidden = false;
        return;
    }
    state.innerHTML = html;
    state.hidden = false;
    content.hidden = true;
}

function initlanding() {
    if (!serviceHealthChecked) {
        serviceHealthChecked = true;
        setState(`<span class="pg-spinner" aria-hidden="true"></span><p>Connecting to the translation service…</p>`);
        checkServiceHealth();
        return;
    }
    renderLanding();
    setState(null);
}

async function checkServiceHealth() {
    try {
        await window.apiService.checkHealth();
        renderLanding();
        setState(null);
    } catch (error) {
        console.error('Service health check failed:', error);
        serviceHealthChecked = false;
        setState(`
            <h2>Can't reach the translation service</h2>
            <p>Check your connection, then try again.</p>
            <button type="button" class="pg-btn pg-btn--primary" id="health-retry">Try again</button>`);
        document.getElementById('health-retry')?.addEventListener('click', () => initlanding());
    }
}

function renderLanding() {
    const content = document.getElementById('landing-content');
    if (!content) return;

    const setup = getTalkSetup();
    const sessions = getSavedSessions()
        .slice()
        .sort((a, b) => new Date(b.timestamp) - new Date(a.timestamp));
    const recent = sessions.slice(0, 3);

    content.innerHTML = `
        <header class="pg-header pd-home-bar">
            <div class="pg-brand">
                <span class="pg-brand__mark" aria-hidden="true">${icons.podium}</span>
                <span class="pg-brand__name">Podium</span>
            </div>
            <button type="button" class="pg-iconbtn" id="btn-settings" aria-label="Settings">${icons.settings}</button>
        </header>
        <main class="pd-home">
            <div class="pd-home__inner">
                <section class="pg-card pd-start" aria-labelledby="new-talk-title">
                    <div>
                        <span class="pg-eyebrow">New talk</span>
                        <h1 id="new-talk-title">Present in one language. The room reads along in theirs.</h1>
                    </div>

                    <div class="pg-field">
                        <label for="home-speaker">I'm speaking</label>
                        <select id="home-speaker" class="pg-select">
                            ${PODIUM_LANGUAGES.map((l) => `<option value="${l.code}"${l.code === setup.speaker ? ' selected' : ''}>${l.name}</option>`).join('')}
                        </select>
                    </div>

                    <fieldset class="pg-field" style="border: 0; padding: 0; margin: 0">
                        <legend>Show translations in</legend>
                        <div class="pg-chips" id="home-targets" style="margin-top: 8px"></div>
                        <p class="pg-hint" id="home-targets-hint" hidden>Pick at least one language.</p>
                    </fieldset>

                    <button type="button" class="pg-btn pg-btn--primary pg-btn--lg pg-btn--block" id="btn-new-session">
                        ${icons.mic}<span>Start talk</span>
                    </button>
                </section>

                <section aria-labelledby="recent-title">
                    <div class="pd-section-head">
                        <h2 id="recent-title">Recent talks</h2>
                        ${sessions.length ? `<button type="button" class="pd-link" id="btn-past-sessions">See all${sessions.length > 3 ? ` (${sessions.length})` : ''}</button>` : ''}
                    </div>
                    ${recent.length ? recent.map((s) => {
                        const d = describeTalk(s);
                        return `<button type="button" class="pd-row" data-session-id="${escapeHtml(s.id)}">
                            <span class="pd-row__text"><span class="pd-row__title">${escapeHtml(d.title)}</span><span class="pd-row__meta">${escapeHtml(d.meta)}</span></span>
                            ${icons.chevron}
                        </button>`;
                    }).join('') : '<p class="pd-empty">Talks you give are saved on this device so you can review or export them later.</p>'}
                </section>
            </div>
        </main>`;

    const speakerSelect = content.querySelector('#home-speaker');
    const targetsBox = content.querySelector('#home-targets');
    const hint = content.querySelector('#home-targets-hint');
    const startButton = content.querySelector('#btn-new-session');
    let current = { ...setup, targets: [...setup.targets] };

    function renderTargets() {
        targetsBox.innerHTML = PODIUM_LANGUAGES
            .filter((l) => l.code !== current.speaker)
            .map((l) => `<button type="button" class="pd-toggle" data-code="${l.code}" aria-pressed="${current.targets.includes(l.code)}">${l.name}</button>`)
            .join('');
        const none = current.targets.length === 0;
        hint.hidden = !none;
        startButton.disabled = none;
    }

    speakerSelect.addEventListener('change', () => {
        const previous = current.speaker;
        current.speaker = speakerSelect.value;
        current.targets = current.targets.filter((c) => c !== current.speaker);
        // Whatever you used to speak is probably wanted on screen now.
        if (previous !== current.speaker && !current.targets.includes(previous)) current.targets.push(previous);
        saveTalkSetup(current);
        renderTargets();
    });

    targetsBox.addEventListener('click', (event) => {
        const button = event.target.closest('[data-code]');
        if (!button) return;
        const code = button.dataset.code;
        current.targets = current.targets.includes(code)
            ? current.targets.filter((c) => c !== code)
            : [...current.targets, code];
        saveTalkSetup(current);
        renderTargets();
    });

    startButton.addEventListener('click', () => {
        saveTalkSetup(current);
        showPage('page-session');
    });

    content.querySelector('#btn-settings').addEventListener('click', () => showPage('page-settings'));
    content.querySelector('#btn-past-sessions')?.addEventListener('click', () => showPage('page-past-sessions'));
    content.querySelectorAll('[data-session-id]').forEach((row) => {
        row.addEventListener('click', () => {
            window.podium.openSessionId = row.dataset.sessionId;
            showPage('page-past-sessions');
        });
    });

    renderTargets();
}

function updateSessionsCount() {
    // The home screen lists recent talks itself; nothing to badge any more.
}

// ---------- Start-up ----------

async function initializeApp() {
    const savedMode = localStorage.getItem('podium-mode') || 'light';
    applyTheme(currentTheme, savedMode);
    setupRoutes();

    try {
        if (window.authGuardService) {
            const hasValidSession = await window.authGuardService.isAuthenticated();
            if (!hasValidSession) {
                // On tafiti.dev this goes to the shared sign-in page and doesn't come back.
                const isAuthenticated = await window.authGuardService.ensureAuthenticated();
                if (!isAuthenticated) {
                    showAuthenticationRequiredScreen();
                    return;
                }
            }
        }

        const pageId = router.getPageIdFromPath(window.location.pathname);
        // A reload on /session would start a talk without the home screen; go home instead.
        showPage(pageId === 'page-session' ? 'page-landing' : pageId, false);
    } catch (error) {
        console.error('Authentication initialization failed:', error);
        showAuthenticationRequiredScreen();
    }
}

function showAuthenticationRequiredScreen() {
    document.querySelectorAll('.page-section').forEach((p) => p.classList.toggle('active', p.id === 'page-landing'));
    setState(`
        <h2>Sign in to use Podium</h2>
        <p>You need to sign in before you can start a talk.</p>
        <button type="button" class="pg-btn pg-btn--primary" id="auth-retry-btn">Sign in with Google</button>`);

    const retryBtn = document.getElementById('auth-retry-btn');
    retryBtn?.addEventListener('click', async () => {
        retryBtn.disabled = true;
        try {
            if (await window.authGuardService?.ensureAuthenticated()) {
                initializeApp();
            }
        } catch (error) {
            console.error('Retry authentication failed:', error);
        } finally {
            retryBtn.disabled = false;
        }
    });
}

document.addEventListener('DOMContentLoaded', () => {
    initializeApp().catch((error) => {
        console.error('App initialization failed:', error);
        showAuthenticationRequiredScreen();
    });
});

window.podium = {
    showPage,
    applyTheme,
    currentPage: () => currentPage,
    currentTheme: () => currentTheme,
    isDarkMode: () => isDarkMode,
    PODIUM_LANGUAGES,
    getSavedSessions,
    updateSessionsCount,
    getTalkSetup,
    describeTalk,
    languageName,
    escapeHtml,
    openSessionId: null,
    router
};

window.showPage = showPage;
window.applyTheme = applyTheme;
window.initlanding = initlanding;
window.PODIUM_LANGUAGES = PODIUM_LANGUAGES;
window.router = router;
